import { range, US_STATES } from '@belong/common';
import { capitalize } from 'es-toolkit/compat';
import {
  AddressModel,
  Location,
  ResidentFavoriteModel,
  UnitBasicInfoModel,
} from 'src/api/models';
import type { GeoPoint, NormalizedFavorites } from './types';

export function joinCityAndState({
  city,
  state,
}: {
  city?: string;
  state?: string;
}) {
  if (!city || !state) return '';
  return `${city.split(' ').join('-')}-${state}`.toLowerCase();
}

export function getRegionOrCityAndState(slug?: string) {
  const result = { regionSlug: '', city: '', state: '', cityAndState: '' };
  if (slug) {
    const splitSlug = slug.split('-');
    const lastSegment = splitSlug[splitSlug.length - 1] ?? '';

    // City and state case
    if (Object.keys(US_STATES).includes(lastSegment.toUpperCase())) {
      result.cityAndState = slug;
      result.state = lastSegment;
      result.city = splitSlug.slice(0, -1).join(' ');
    } else {
      // Region case
      result.regionSlug = slug;
    }
  }

  return result;
}

// Optional because every read below already goes through `?.`, and callers
// hand it a unit's `basicInfo`, which the models mark optional.
export function getBathRoomString(basicInfo?: UnitBasicInfoModel): string {
  const totalBathCount =
    (basicInfo?.fullBathroomCount ?? 0) +
      0.5 * (basicInfo?.halfBathroomCount || 0) || 0;
  if (!totalBathCount) {
    return '0 Baths';
  }

  if (basicInfo?.fullBathroomCount && basicInfo?.halfBathroomCount === 1) {
    return `${basicInfo?.fullBathroomCount}.5 Bath`;
  }

  const bathString = `${basicInfo?.fullBathroomCount} ${
    basicInfo?.halfBathroomCount
      ? `${basicInfo?.fullBathroomCount ? ' + ' : ' '}${basicInfo?.halfBathroomCount} ½`
      : ''
  } Bath`;
  return bathString;
}

export function getFormattedBeds<T extends UnitBasicInfoModel>(
  basicInfo?: T,
  showUnitLevel = false,
): string {
  const { bedroomCount, detailBasic } = basicInfo || {};
  const { unitType, unitLevel } = detailBasic || {};

  let formattedBeds = '';

  if (bedroomCount) {
    formattedBeds += `${bedroomCount} Bed`;
  }

  if (showUnitLevel && unitLevel && unitLevel !== 'None') {
    formattedBeds += formattedBeds
      ? ` ${unitLevel.toLowerCase()}`
      : capitalize(unitLevel);
  }

  if (unitType && unitType !== 'None') {
    formattedBeds += formattedBeds
      ? ` ${capitalize(unitType)}`
      : capitalize(unitType);
  } else if (!bedroomCount) {
    formattedBeds += formattedBeds ? ' studio' : 'Studio';
  }

  return formattedBeds;
}

export function getCityOptions(availableLocations: Location[]) {
  return availableLocations.map((location) => ({
    label: `${location.city}, ${location.state}`,
    value: joinCityAndState(location),
  }));
}

export function genNumericOptions(amount: number) {
  return range(1, amount + 1).map((number) => ({
    value: String(number),
    label: `${number}+`,
  }));
}

const getPixelPerMeter = (lat: number, zoom: number) =>
  (156543.03392 * Math.cos((lat * Math.PI) / 180)) / 2 ** zoom;

const rad = (val: number) => (val * Math.PI) / 180;

// Haversine Formula
const getDistance = (p1: GeoPoint, p2: GeoPoint) => {
  const earthRadius = 6378137; // Earth’s mean radius in meter
  const distanceLat = rad(p2.lat - p1.lat);
  const distanceLong = rad(p2.lng - p1.lng);
  const a =
    Math.sin(distanceLat / 2) * Math.sin(distanceLong / 2) +
    Math.cos(rad(p1.lat)) *
      Math.cos(rad(p2.lat)) *
      Math.sin(distanceLong / 2) *
      Math.sin(distanceLong / 2);

  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

  // returns the distance in meter
  return earthRadius * c;
};

type Position = { top?: string; left?: string };

const getPlacement = (p1: GeoPoint, p2: GeoPoint) => {
  const position: Position = {};

  if (p1.lat - p2.lat >= 0) {
    position.top = '0';
  }

  if (p1.lat - p2.lat < 0) {
    position.top = '-250px';
  }

  if (p1.lng - p2.lng >= 0) {
    position.left = '-250px';
  }
  if (p1.lng - p2.lng < 0) {
    position.left = '0';
  }

  return position;
};

export const getMarkerPosition = ({
  lat,
  lng,
  currentZoom,
  currentCenter,
}: {
  lat: number;
  lng: number;
  currentZoom: number;
  currentCenter: GeoPoint;
}) => {
  const pixelDistance = getPixelPerMeter(lat, currentZoom);

  const distanceInMeters = getDistance({ lat, lng }, currentCenter);

  // NaN signifies a decimal point precision error that is currently always going to be in the NW corner and definitely not in range.
  // I do not have an elegant solution without spending significanty more time optimizing the algo with bigNum.
  if (
    distanceInMeters / pixelDistance > 150 ||
    Number.isNaN(distanceInMeters)
  ) {
    return getPlacement({ lat, lng }, currentCenter);
  }
};

export function getAddressStringCompact({
  city,
  state,
  streetAddress,
  unitNumber,
}: AddressModel) {
  if (!streetAddress) {
    return `${city}, ${state}`;
  }

  const isNumeric = !Number.isNaN(Number(unitNumber));
  let unitString = '';
  if (unitNumber && isNumeric) {
    unitString = ` #${unitNumber}`;
  } else if (unitNumber && !isNumeric) {
    unitString = ` ${unitNumber}`;
  }

  return `${streetAddress}${unitString}, ${city}`;
}

export const getNormalizedFavoritesByHomeId = (
  favorites: ResidentFavoriteModel[],
) =>
  favorites
    ?.filter((val) => !!val.basicInfo?.unitId)
    .reduce((acc: NormalizedFavorites, val) => {
      acc[val.basicInfo?.unitId || ''] = val;
      return acc;
    }, {} as NormalizedFavorites) || {};
